import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Edit, Trash2, Eye, FileQuestion, Clock, 
  Search, X, Save, Users, CheckCircle, AlertCircle
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeacherQuizzes() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [quizzes, setQuizzes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(searchParams.get('action') === 'add');
  const [editQuiz, setEditQuiz] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);

  const [formData, setFormData] = useState({
    title: '', description: '', course_id: '', subject_id: '',
    duration_minutes: 30, total_marks: 100, passing_marks: 40,
    start_date: '', end_date: '', shuffle_questions: true,
    show_result_immediately: true, questions: []
  });

  const [questionForm, setQuestionForm] = useState({
    question: '', type: 'mcq', options: ['', '', '', ''], correct_answer: '', marks: 1
  });

  useEffect(() => { fetchData(); }, []);

  const getHeaders = () => {
    const token = localStorage.getItem('staffToken');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) { navigate('/staff-portal'); return; }
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [quizzesRes, coursesRes] = await Promise.all([
        fetch(`${API_URL}/api/lms/quizzes`, { headers }).then(r => r.json()).catch(() => []),
        fetch(`${API_URL}/api/lms/teacher/courses`, { headers }).then(r => r.json()).catch(() => [])
      ]);

      setQuizzes(Array.isArray(quizzesRes) ? quizzesRes : []);
      setCourses(Array.isArray(coursesRes) ? coursesRes : []);
    } catch (error) {
      toast.error('Failed to load quizzes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.course_id) {
      toast.error('Please fill required fields');
      return;
    }

    setSaving(true);
    try {
      const url = editQuiz ? `${API_URL}/api/lms/quizzes/${editQuiz.id}` : `${API_URL}/api/lms/quizzes`;
      const response = await fetch(url, {
        method: editQuiz ? 'PUT' : 'POST',
        headers: getHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editQuiz ? 'Quiz updated!' : 'Quiz created!');
        setShowModal(false);
        resetForm();
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save quiz');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this quiz?')) return;
    try {
      await fetch(`${API_URL}/api/lms/quizzes/${id}`, { method: 'DELETE', headers: getHeaders() });
      toast.success('Quiz deleted!');
      fetchData();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', description: '', course_id: '', subject_id: '',
      duration_minutes: 30, total_marks: 100, passing_marks: 40,
      start_date: '', end_date: '', shuffle_questions: true,
      show_result_immediately: true, questions: []
    });
    setEditQuiz(null);
  };

  const addQuestion = () => {
    if (!questionForm.question) {
      toast.error('Enter question text');
      return;
    }
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, { ...questionForm, id: Date.now() }]
    }));
    setQuestionForm({ question: '', type: 'mcq', options: ['', '', '', ''], correct_answer: '', marks: 1 });
    toast.success('Question added');
  };

  const removeQuestion = (id) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== id)
    }));
  };

  const openEdit = (quiz) => {
    setEditQuiz(quiz);
    setFormData({
      title: quiz.title || '', description: quiz.description || '', course_id: quiz.course_id || '',
      subject_id: quiz.subject_id || '', duration_minutes: quiz.duration_minutes || 30,
      total_marks: quiz.total_marks || 100, passing_marks: quiz.passing_marks || 40,
      start_date: quiz.start_date || '', end_date: quiz.end_date || '',
      shuffle_questions: quiz.shuffle_questions ?? true, show_result_immediately: quiz.show_result_immediately ?? true,
      questions: quiz.questions || []
    });
    setShowModal(true);
  };

  const filteredQuizzes = quizzes.filter(q => q.title?.toLowerCase().includes(searchTerm.toLowerCase()));

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/staff-portal/lms" className="p-2 hover:bg-indigo-600 rounded-lg"><ArrowLeft className="h-5 w-5" /></Link>
              <div><h1 className="text-xl font-bold">Quizzes & Tests</h1><p className="text-indigo-200 text-sm">Create and manage quizzes</p></div>
            </div>
            <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-white text-indigo-700 hover:bg-indigo-50"><Plus className="h-4 w-4 mr-2" />Create Quiz</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <Card><CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input placeholder="Search quizzes..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
        </CardContent></Card>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredQuizzes.length === 0 ? (
            <Card className="col-span-full"><CardContent className="p-12 text-center">
              <FileQuestion className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No quizzes created yet</p>
            </CardContent></Card>
          ) : (
            filteredQuizzes.map(quiz => (
              <Card key={quiz.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-2 bg-purple-100 rounded-lg"><FileQuestion className="h-5 w-5 text-purple-600" /></div>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">{quiz.questions?.length || 0} Questions</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{quiz.title}</h3>
                  <p className="text-sm text-gray-500 mb-3">{quiz.course_name}</p>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{quiz.duration_minutes} min</span>
                    <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3" />{quiz.total_marks} marks</span>
                  </div>
                  <div className="flex gap-2 pt-3 border-t">
                    <Button variant="outline" size="sm" onClick={() => openEdit(quiz)} className="flex-1"><Edit className="h-4 w-4 mr-1" />Edit</Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(quiz.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>

      {/* Create/Edit Quiz Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">{editQuiz ? 'Edit Quiz' : 'Create Quiz'}</h2>
              <button onClick={() => { setShowModal(false); resetForm(); }}><X className="h-5 w-5" /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div><Label>Title *</Label><Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} required /></div>
              
              <div><Label>Description</Label><textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" rows={2} /></div>
              
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Course *</Label>
                  <Select value={formData.course_id} onValueChange={(v) => setFormData(prev => ({ ...prev, course_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{courses.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div><Label>Duration (minutes)</Label><Input type="number" value={formData.duration_minutes} onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 30 }))} /></div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Total Marks</Label><Input type="number" value={formData.total_marks} onChange={(e) => setFormData(prev => ({ ...prev, total_marks: parseInt(e.target.value) || 100 }))} /></div>
                <div><Label>Passing Marks</Label><Input type="number" value={formData.passing_marks} onChange={(e) => setFormData(prev => ({ ...prev, passing_marks: parseInt(e.target.value) || 40 }))} /></div>
              </div>

              {/* Questions Section */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Questions ({formData.questions.length})</h3>
                
                {/* Add Question */}
                <div className="bg-gray-50 p-3 rounded-lg space-y-3">
                  <div><Label>Question</Label><Input value={questionForm.question} onChange={(e) => setQuestionForm(prev => ({ ...prev, question: e.target.value }))} placeholder="Enter question" /></div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Type</Label>
                      <Select value={questionForm.type} onValueChange={(v) => setQuestionForm(prev => ({ ...prev, type: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mcq">Multiple Choice</SelectItem>
                          <SelectItem value="true_false">True/False</SelectItem>
                          <SelectItem value="short">Short Answer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Marks</Label><Input type="number" value={questionForm.marks} onChange={(e) => setQuestionForm(prev => ({ ...prev, marks: parseInt(e.target.value) || 1 }))} /></div>
                  </div>
                  
                  {questionForm.type === 'mcq' && (
                    <div className="space-y-2">
                      <Label>Options</Label>
                      {questionForm.options.map((opt, idx) => (
                        <Input key={idx} value={opt} onChange={(e) => {
                          const newOpts = [...questionForm.options];
                          newOpts[idx] = e.target.value;
                          setQuestionForm(prev => ({ ...prev, options: newOpts }));
                        }} placeholder={`Option ${idx + 1}`} />
                      ))}
                    </div>
                  )}
                  
                  <div><Label>Correct Answer</Label><Input value={questionForm.correct_answer} onChange={(e) => setQuestionForm(prev => ({ ...prev, correct_answer: e.target.value }))} placeholder={questionForm.type === 'mcq' ? 'Enter correct option (e.g., Option 1)' : 'Enter correct answer'} /></div>
                  
                  <Button type="button" onClick={addQuestion} variant="outline" className="w-full"><Plus className="h-4 w-4 mr-2" />Add Question</Button>
                </div>
                
                {/* Question List */}
                {formData.questions.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {formData.questions.map((q, idx) => (
                      <div key={q.id} className="flex items-center justify-between p-2 bg-white border rounded">
                        <span className="text-sm"><strong>Q{idx + 1}:</strong> {q.question.slice(0, 50)}...</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeQuestion(q.id)} className="text-red-600"><X className="h-4 w-4" /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1">Cancel</Button>
                <Button type="submit" disabled={saving} className="flex-1">{saving ? 'Saving...' : <><Save className="h-4 w-4 mr-2" />{editQuiz ? 'Update' : 'Create'}</>}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
